import dynamic from 'next/dynamic';
import { downloadLinks } from '@/common/components/footer/constants';
import { HeaderV2 } from '@/common/components/header-v2';
import CategoryCarousel from './components/category-carousel.component';
import { MainBanner } from './components/main-banner.component';
import MobileBottomNavigation from './components/mobile-bottom-navigation.component';
import ProductCarousel from './components/product-carousel.component';
import { TopBanner } from './components/top-banner.component';
import type {
  ShopBannerCarouselSection,
  ShopHomeData,
  ShopProductCarouselSection,
} from './data/shop-home.mappers';

function SectionPlaceholder({ className }: { className: string }) {
  return <div className={className} aria-hidden />;
}

const BannerProductCarousel = dynamic(
  async () => await import('./components/banner-product-carousel.component'),
  {
    ssr: false,
    loading: () => (
      <SectionPlaceholder className="min-h-[300px] w-full md:min-h-[340px]" />
    ),
  }
);
const VendorCtaBanner = dynamic(
  async () => await import('./components/vendor-cta-banner.component'),
  {
    ssr: false,
    loading: () => (
      <SectionPlaceholder className="min-h-[94px] w-full px-4 md:min-h-[160px] md:px-0" />
    ),
  }
);
const NewestStores = dynamic(
  async () => await import('./components/newest-stores.component'),
  {
    ssr: false,
    loading: () => (
      <SectionPlaceholder className="min-h-[220px] w-full md:min-h-[260px]" />
    ),
  }
);
const ShopUserRecommendations = dynamic(
  async () => await import('./components/shop-user-recommendations.component'),
  {
    ssr: false,
    loading: () => (
      <SectionPlaceholder className="min-h-[280px] w-full md:min-h-[320px]" />
    ),
  }
);
const DownloadAppCta = dynamic(
  async () => await import('./components/download-app-cta.component'),
  {
    ssr: false,
    loading: () => (
      <SectionPlaceholder className="min-h-[58px] w-full md:min-h-[144px]" />
    ),
  }
);
const LatestProductsList = dynamic(
  async () => await import('./components/latest-products-list.component'),
  {
    ssr: false,
    loading: () => (
      <SectionPlaceholder className="min-h-[320px] w-full md:min-h-[360px]" />
    ),
  }
);
const ShopFooter = dynamic(
  async () => await import('./components/shop-footer.component'),
  {
    ssr: false,
    loading: () => <SectionPlaceholder className="min-h-[320px] w-full" />,
  }
);

interface ShopFeatureProps {
  data: ShopHomeData;
}

export const SHOP_HOME_HEADING =
  'فروشگاه اینترنتی ویترو؛ خرید آنلاین محصولات متنوع';

const CAROUSEL_ICONS = {
  specialOffer: '/assets/images/webp/shop-carousels/special-offer.webp',
  vitrinOff: '/assets/images/webp/shop-carousels/hot-offer.webp',
  selectedProducts: '/assets/images/webp/shop-carousels/featured-products.webp',
} as const;

function ColoredProductCarousel({
  section,
  icon,
  backgroundColor,
}: {
  section: ShopProductCarouselSection;
  icon: string;
  backgroundColor: string;
}) {
  return (
    <ProductCarousel
      title={section.title}
      icon={icon}
      backgroundColor={backgroundColor}
      viewAllHref={section.viewAllHref}
      products={section.products}
    />
  );
}

function HomeBannerCarousel({
  section,
}: {
  section: ShopBannerCarouselSection;
}) {
  return (
    <BannerProductCarousel
      title={section.title}
      desktopBanner={section.desktopBanner}
      mobileBanner={section.mobileBanner}
      bannerAlt={section.bannerAlt}
      bannerHref={section.bannerHref}
      viewAllHref={section.viewAllHref}
      products={section.products}
    />
  );
}

export default function ShopFeature({ data }: ShopFeatureProps) {
  return (
    <div className="bg-gray-30 [overflow-anchor:none]">
      {data.topBanners.length > 0 && <TopBanner banners={data.topBanners} />}
      <HeaderV2 />
      <main id="main-content" className="bg-gray-30 pb-16 lg:pb-0">
        <h1 className="sr-only">{SHOP_HOME_HEADING}</h1>

        {data.mainBanner.slides.length > 0 && (
          <div className="mb-6 min-h-[200px] md:mb-10 md:min-h-0 md:aspect-[1440/493]">
            <MainBanner
              slides={data.mainBanner.slides}
              duration={data.mainBanner.duration}
            />
          </div>
        )}
        <div className="space-y-6 pb-6 md:mx-auto md:max-w-[1200px] md:space-y-10 md:pb-10">
          {data.categories.length > 0 ? (
            <section
              aria-labelledby="shop-home-categories-title"
              className="min-h-14 md:min-h-[120px]"
            >
              <h2 id="shop-home-categories-title" className="sr-only">
                دسته‌بندی‌های فروشگاه ویترو
              </h2>
              <CategoryCarousel
                className="pr-4 md:px-0"
                categories={data.categories}
                plpPath="/products"
                categoryQueryKey="category"
              />
            </section>
          ) : null}
          {data.specialOffer && (
            <ColoredProductCarousel
              section={data.specialOffer}
              icon={CAROUSEL_ICONS.specialOffer}
              backgroundColor="#FA8C0A"
            />
          )}
          {data.bannerCarouselOne && (
            <HomeBannerCarousel section={data.bannerCarouselOne} />
          )}
          <VendorCtaBanner />
          {data.newestStores.length > 0 && (
            <NewestStores
              stores={data.newestStores}
              viewAllHref={data.newestStoresViewAllHref}
            />
          )}
          {data.vitrinOff && (
            <ColoredProductCarousel
              section={data.vitrinOff}
              icon={CAROUSEL_ICONS.vitrinOff}
              backgroundColor="#A81710"
            />
          )}
          {data.bannerCarouselTwo && (
            <HomeBannerCarousel section={data.bannerCarouselTwo} />
          )}
          {data.selectedProducts && (
            <ColoredProductCarousel
              section={data.selectedProducts}
              icon={CAROUSEL_ICONS.selectedProducts}
              backgroundColor="#14385D"
            />
          )}
          <ShopUserRecommendations />
          {data.bannerCarouselThree && (
            <HomeBannerCarousel section={data.bannerCarouselThree} />
          )}
          <div className="px-4 md:px-0">
            <DownloadAppCta
              bazaarHref={downloadLinks.bazaar}
              directDownloadHref={downloadLinks.bazaar}
              appIcon="/assets/images/svg/logo-white-orange.svg"
              bazaarLogo="/assets/images/svg/bazaar.svg"
            />
          </div>
          {data.latestProducts.length > 0 && (
            <LatestProductsList
              title={data.latestProductsTitle}
              products={data.latestProducts}
              viewAllHref={data.latestProductsViewAllHref}
              className="mb-5 md:mb-6"
            />
          )}
        </div>
      </main>
      <ShopFooter offsetMobileNav />
      <MobileBottomNavigation />
    </div>
  );
}
